import { useCallback, useMemo, useRef, useState } from 'react'
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperClass } from 'swiper'
import findIndex from 'lodash/findIndex'
import { CoverLetter, Resume } from '@rio/types'
import { ColorChangeHandler, selectors, TemplateChangeHandler } from 'builder/modules/resumeEditor'

import { templateGroups } from 'builder/utils/templateGroups'
import { formatImageUrl } from 'builder/utils/formatImageUrl'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useTourConfig } from 'builder/components/DocumentEditor/DocumentTour'

import { ColorSelection } from '../ColorSelection/ColorSelection'
import { useTemplateCustomization } from '../../hooks/useTemplateCustomization'
import { ColorPickerPanel } from '../ColorPickerPanel/ColorPickerPanel'
import {
  Container,
  TemplateSelectionMobileContainer,
  TemplateContainer,
  Template,
  Templates,
  TemplatePreview,
  BadgeWrapper,
  PlanBadges,
  FormatBadges,
} from './styles'

interface TemplateSelectionMobileProps {
  currentDocument: Resume | CoverLetter
  updateTemplate: TemplateChangeHandler
  handleColorSelect: ColorChangeHandler
}

export const TemplateSelectionMobile = (props: TemplateSelectionMobileProps) => {
  const { currentDocument, updateTemplate, handleColorSelect } = props
  const { selectTemplate } = useTourConfig()
  const [isColorPickerOpen, setColorPickerOpen] = useState<boolean>(false)
  const templateListRef = useRef<HTMLDivElement | null>(null)
  const templatesGallery = useRef<SwiperClass | null>(null)
  const editorState = useTypedSelector(state => state.generalEditor)
  const locale = useTypedSelector(selectors.locale)
  const templates = templateGroups(editorState, currentDocument.type, locale)?.[0].templates
  const { currentTemplate } = useTemplateCustomization(currentDocument)

  const initialSlide = useMemo(
    () => findIndex(templates, { id: currentTemplate?.id }),
    [currentTemplate, templates],
  )

  const handleTemplateChange = useCallback(
    (swiper: SwiperClass) => {
      if (!swiper.clickedIndex) return // Prevent execution on initialization
      const { activeIndex = 0 } = swiper
      const { id, supportsCustomColor } = templates[activeIndex]
      updateTemplate(id)
      selectTemplate(supportsCustomColor)
    },
    [templates, updateTemplate, selectTemplate],
  )

  return (
    <Container>
      {!isColorPickerOpen ? (
        <>
          <TemplateSelectionMobileContainer id="resume-templates-container" ref={templateListRef}>
            <Templates>
              <ReactSwiper
                onSwiper={swiper => (templatesGallery.current = swiper)}
                initialSlide={initialSlide}
                slidesPerView="auto"
                spaceBetween={0}
                centeredSlides={true}
                slideToClickedSlide={true}
                onSlideChange={handleTemplateChange}
              >
                {templates?.map((template: { id: string; name: string; thumbnail: string }) => (
                  <SwiperSlide key={template.id}>
                    <TemplateContainer>
                      <TemplatePreview
                        isActive={currentTemplate?.id === template.id}
                        image={formatImageUrl(template.thumbnail, { width: 154 })}
                      />
                      <Template>{template.name}</Template>
                    </TemplateContainer>
                  </SwiperSlide>
                ))}
              </ReactSwiper>
              <BadgeWrapper>
                {!currentTemplate?.premium && <PlanBadges badges={['Free']} />}
                <FormatBadges badges={currentTemplate?.supportedFormats || []} />
              </BadgeWrapper>
            </Templates>
          </TemplateSelectionMobileContainer>
          <ColorSelection
            isScrolled={true}
            handleColorPickerOpen={() => setColorPickerOpen(true)}
            {...{
              currentDocument,
              handleColorSelect,
            }}
          />
        </>
      ) : (
        <ColorPickerPanel
          currentDocument={currentDocument}
          handleColorSelect={handleColorSelect}
          handleClose={() => setColorPickerOpen(false)}
        />
      )}
    </Container>
  )
}
